import { OfferPriority } from './../../models/class/offerPriority/offerPriority';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FullOffer, ImageOffer } from 'app/shared/models/class/offer/offer';
import { PricingOffer } from 'app/shared/models/class/pricing-offer/pricingOffer';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';

import * as FileSaver from 'file-saver';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class OfferService {

  constructor(
    private http: HttpClient
  ) { }

  /* -------------------------------------------------------------------------- */
  /*                                     GET                                    */
  /* -------------------------------------------------------------------------- */

  getOffer(structure?: string, universe?: string): Observable<FullOffer[]> {
    return this.http.get<FullOffer[]>(`${environment.apiUrl}/offer/offers/${structure ? structure : ' '}/${universe}`);
  }

  getFullOffer(clubCode: string = null): Observable<FullOffer[]> {
    const param = clubCode ? clubCode : '';
    return this.http.get<FullOffer[]>(`${environment.apiUrl}/Offer/FullOffers/${param}`);
  }

  getOfferById(offerKey: number): Observable<FullOffer> {
    return this.http.get<FullOffer>(`${environment.apiUrl}/Offer/FullOfferByOfferKey/${offerKey}`);
  }

  getOfferByClub(clubCode: string): Observable<FullOffer[]> {
    return this.http.get<FullOffer[]>(`${environment.apiUrl}/Offer/OffersByClub/${clubCode}`);
  }

  getImgOffer(offerKey: number): Observable<ImageOffer> {
    return this.http.get<ImageOffer>(`${environment.apiUrl}/Offer/GetPicture/${offerKey}`);
  }

  getCarouselPictures(offerKey: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/Offer/GetCarouselPictures/${offerKey}`);
  }

  getAltOfferPictures(offerKey: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/Offer/AltOfferPicture/${offerKey}`);
  }

  getOfferPicturesZip(offerKey: number): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/Offer/OfferPicturesZip/${offerKey}`, { observe: 'response', responseType: 'blob' })
      .pipe(map(res => this.saveFile(res, offerKey + '_pictures')));
  }
  // GET main pictures all offers
  getOfferMainPicturesZip(): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/Offer/OfferMainPictureZip`, { observe: 'response', responseType: 'blob' })
      .pipe(map(res => this.saveFile(res, 'OfferMainPictures')));
  }


  /* -------------------------------------------------------------------------- */
  /*                                    POST                                    */
  /* -------------------------------------------------------------------------- */

  addOffer(newOffer: FullOffer): Observable<any> {
    const formData = new FormData();
    formData.append('offerString', JSON.stringify(newOffer));

    return this.http.post<any>(`${environment.apiUrl}/Offer/NewOffer`, formData);
  }

  getOfferTag(tags: string, coordinates: string, structure?: string, universe?): Observable<FullOffer[]> {
    const formData = new FormData();
    formData.append('tags', tags);
    formData.append('coordinates', coordinates);

    const uni = !universe ? 1 : universe;

    let clubCode = sessionStorage.getItem("clubCodeForHomeOffers");

    return this.http.post<FullOffer[]>(`${environment.apiUrl}/Offer/taggedOffersTest/${structure ? structure : ' '}/${universe}${clubCode ? '/' + clubCode : ''}`, formData);
  }

  addOfferPricing(pricing: PricingOffer) {
    return this.http.post(`${environment.apiUrl}/Offer/NewOfferPricing`, pricing);
  }

  uploadImg(offerKey: number, files: File[], isMainPicture?: boolean) {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file);
    }

    const requestOptions: object = { responseType: 'text' };

    return this.http.post<any>(`${environment.apiUrl}/Offer/UploadPicture?offerKey=${offerKey}&&isMainPicture=${isMainPicture}`, formData, requestOptions);
  }

  uploadImages(offerKey: number, files: File[], isMainPicture?: boolean) {
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file);
    }

    return this.http.post<any>(`${environment.apiUrl}/Offer/UploadPicture?offerKey=${offerKey}&&isMainPicture=${false}`, formData);
  }


  /* -------------------------------------------------------------------------- */
  /*                                     PUT                                    */
  /* -------------------------------------------------------------------------- */

  updateOffer(newOffer: FullOffer, file?: File): Observable<any> {

    const formData = new FormData();
    formData.append('offerString', JSON.stringify(newOffer));
    // file !== null ?
    //   formData.append('file', file, file.name) :
    //   formData.append('file', null);

    return this.http.put<any>(`${environment.apiUrl}/Offer/UpdateOffer`, formData);
  }

  activateOrDeactivateOffer(offerKey: number, isActive: boolean) {
    return this.http.put(`${environment.apiUrl}/Offer/ActivateOffer/${offerKey}/${isActive}`, null);
  }


  prioritizeOffer(offerKey: number, isPriority: boolean) {
    return this.http.put(`${environment.apiUrl}/Offer/PrioritizeOffer/${offerKey}/${isPriority}`, null);
  }

  updatePriorityOffers(updatePriority: OfferPriority) {
    const formData = new FormData();

    formData.append('clubCode', updatePriority.clubCode);
    formData.append('offerKeys', updatePriority.offerKeys);

    return this.http.put(`${environment.apiUrl}/Offer/UpdatePriorityOffers`, formData);
  }

  updateOfferPricing(pricing: PricingOffer) {
    return this.http.put(`${environment.apiUrl}/Offer/UpdateOfferPricing`, pricing);
  }

  updateExternalOffer(offerKey: number, isExternalOffer: boolean) {
    return this.http.put(`${environment.apiUrl}/Offer/ExternalOffer/${offerKey}/${isExternalOffer}`, null);
  }

  updateOfferHasSession(offerKey: number, hasSession: boolean) {
    return this.http.put(`${environment.apiUrl}/Offer/OfferHasSession/${offerKey}/${hasSession}`, null);
  }

  /* -------------------------------------------------------------------------- */
  /*                                   DELETE                                   */
  /* -------------------------------------------------------------------------- */

  deleteOffer(offerKey: number) {
    return this.http.delete(`${environment.apiUrl}/offer/${offerKey}`);
  }

  deleteAltPictureOffer(offerPictureKey: number) {
    return this.http.delete(`${environment.apiUrl}/Offer/AltOfferPicture/${offerPictureKey}`);
  }

  /*
  ------------------------------- FILESAVER ----------------------------------
  */

  private saveFile(res: HttpResponse<any>, fileName: string): Blob {
    FileSaver.saveAs(res.body, fileName);
    return res.body;
  }
}

